import { createSlice, createSelector } from "@reduxjs/toolkit";
import { FEATURES_CONFIG } from "../../featuresConfig";
import { rgsPlay } from "./spinSlice";
import { Settings } from "../../settings";

// Define the initial state for the user interface slice
// This state keeps track of which UI elements and popups are currently open
const initialState = {
  introPopupActive: true, // Controls the intro popup (open at game start)
  betSelectPopupActive: false, // Controls the bet amount selection popup
  autoplayPopupActive: false, // Controls the autoplay configuration popup
  leaveGamePopupActive: false, // Controls the leave game confirmation popup
  menuActive: false, // Controls the menu popup
  gameRulesActive: false, // Controls the game rules popup
  paytableActive: false, // Controls the paytable popup
  settingsActive: false, // Controls the settings popup
  replayActive: false, // Controls the replay/history popup
  replayEnterActive: false, // Controls the Enter modal nested inside the replay/history popup
  replayEndedActive: false, // Controls the Ended modal nested inside the replay/history popup
  creditsPopupActive: false, // Controls the credits popup
  expirationPopupActive: false, // Controls the session expiration popup
  userInterfaceDisabled: true, // Controls global UI disable (disabled during big events like free spins, win ]animations)
  buyFreeSpinsPopupActive: false, // Controls the buy free spins popup
  connectionPopupActive: false, // Controls the connection popup
  soundAdjustmentPopupActive: false, // Controls the sound adjustment popup
  symbolPopupActive: null, // Controls which symbol popup is currently open (null = none, symbolId = specific symbol)
  symbolPopupBlocked: false, // Controls if symbol popup feature is blocked from opening
  miniPaytablePopupActive: false, // Controls if MiniPaytable popup is currently open (symbol info popup on reels)
  turboSuggestionPopupActive: false, // Controls the turbo suggestion popup (shown after 10 manual stops)
  bonusBuyActive: false, // Controls the bonus buy popup
  holdAndWinResumePopupActive: false, // Controls the Hold and Win resume popup (reload during feature)
  errorPopupActive: false, // Controls the error popup (API errors like insufficient credits)
  errorPopupCode: null, // Stores the error code to display in popup (e.g., 301)
  infoPopupActive: false, // Controls the info popup (informational messages without OK button)
  infoPopupKey: null, // Stores the translation key for info popup message
  infoPopupData: {}, // Stores dynamic data for info popup interpolation (e.g., {count: 10, total_win: "100.00"})
  iosFullscreenOverlayActive: false, // Controls iOS Safari fullscreen swipe overlay
};

// Create a slice for user interface using Redux Toolkit's `createSlice` function
// A slice is a collection of Redux reducer logic and actions for a specific feature of the application
const userInterfaceSlice = createSlice({
  // Name of the slice, used to prefix generated action types
  name: "userInterface",

  // The initial state of the slice, defined above
  initialState,

  // Reducers are functions that handle actions and update the state
  reducers: {
    /**
     * Reducer to open the bet selector popup.
     * Sets betSelectPopupActive to true.
     */
    openBetSelectPopup(state) {
      state.symbolPopupActive = null;
      state.betSelectPopupActive = true;
    },

    /**
     * Reducer to close the bet selector popup.
     * Sets betSelectPopupActive to false.
     */
    closeBetSelectPopup(state) {
      state.betSelectPopupActive = false;
    },

    /**
     * Reducer to close the intro popup.
     * Sets introPopupActive to false.
     */
    closeIntroPopup(state) {
      state.introPopupActive = false;
    },

    /**
     * Reducer to open the autoplay configuration popup.
     * Sets autoplayPopupActive to true.
     */
    openAutoplayPopup(state) {
      state.symbolPopupActive = null;
      state.autoplayPopupActive = true;
    },

    /**
     * Reducer to close the autoplay configuration popup.
     * Sets autoplayPopupActive to false.
     */
    closeAutoplayPopup(state) {
      state.autoplayPopupActive = false;
    },

    /**
     * Reducer to open the leave game confirmation popup.
     * Sets leaveGamePopupActive to true.
     */
    openLeaveGamePopup(state) {
      state.symbolPopupActive = null;
      state.leaveGamePopupActive = true;
    },

    /**
     * Reducer to close the leave game confirmation popup.
     * Sets leaveGamePopupActive to false.
     */
    closeLeaveGamePopup(state) {
      state.leaveGamePopupActive = false;
    },

    /**
     * Reducer to open the menu popup.
     * Primary theme: auto-opens settings (game rules/paytable are HTML sections).
     * Secondary theme: opens menu only, user selects individual sections.
     */
    openMenu(state) {
      state.symbolPopupActive = null;
      state.menuActive = true;
      state.gameRulesActive = false;
      state.paytableActive = false;
      state.settingsActive = Settings.APP.UI_THEME === "primary";
      state.replayActive = false;
    },

    /**
     * Reducer to open the bonus buy popup.
     * Sets bonusBuyActive to true.
     */
    openBonusBuy(state) {
      state.symbolPopupActive = null;
      state.bonusBuyActive = true;
    },

    /**
     * Reducer to close the bonus buy popup.
     * Sets bonusBuyActive to false.
     */
    closeBonusBuy(state) {
      state.bonusBuyActive = false;
    },

    /**
     * Reducer to close the menu popup.
     * Sets menuActive to false and closes all child menu items.
     */
    closeMenu(state) {
      state.menuActive = false;
      state.gameRulesActive = false;
      state.paytableActive = false;
      state.settingsActive = false;
      state.replayActive = false;
    },

    /**
     * Reducer to open the game rules popup.
     * Sets gameRulesActive to true and closes paytable, settings and replay.
     */
    openGameRules(state) {
      state.symbolPopupActive = null;
      state.gameRulesActive = true;
      state.paytableActive = false;
      state.settingsActive = false;
      state.replayActive = false;
    },

    /**
     * Reducer to close the game rules popup.
     * Sets gameRulesActive to false.
     */
    closeGameRules(state) {
      state.gameRulesActive = false;
    },

    /**
     * Reducer to open the paytable popup.
     * Sets paytableActive to true and closes game rules, settings and replay.
     */
    openPaytable(state) {
      state.symbolPopupActive = null;
      state.paytableActive = true;
      state.gameRulesActive = false;
      state.settingsActive = false;
      state.replayActive = false;
    },

    /**
     * Reducer to close the paytable popup.
     * Sets paytableActive to false.
     */
    closePaytable(state) {
      state.paytableActive = false;
    },

    /**
     * Reducer to open the settings popup.
     * Sets settingsActive to true and closes game rules, paytable and replay.
     */
    openSettings(state) {
      state.symbolPopupActive = null;
      state.settingsActive = true;
      state.gameRulesActive = false;
      state.paytableActive = false;
      state.replayActive = false;
    },

    /**
     * Reducer to close the settings popup.
     * Sets settingsActive to false.
     */
    closeSettings(state) {
      state.settingsActive = false;
    },

    /**
     * Reducer to open the replay/history popup.
     * Sets replayActive to true and closes game rules, paytable and settings.
     */
    openReplay(state) {
      state.symbolPopupActive = null;
      state.replayActive = true;
      state.gameRulesActive = false;
      state.paytableActive = false;
      state.settingsActive = false;
    },

    /**
     * Reducer to close the replay/history popup.
     * Sets replayActive to false.
     */
    closeReplay(state) {
      state.replayActive = false;
    },

    /**
     * Reducer to open the Enter modal nested inside the replay/history popup
     * (shown when a history row's Play button is clicked).
     */
    openReplayEnter(state) {
      state.replayEnterActive = true;
    },

    /**
     * Reducer to close the Enter modal nested inside the replay/history popup.
     */
    closeReplayEnter(state) {
      state.replayEnterActive = false;
    },

    /**
     * Reducer to open the Ended modal nested inside the replay/history popup
     * (shown once a replay finishes playing).
     */
    openReplayEnded(state) {
      state.replayEndedActive = true;
    },

    /**
     * Reducer to close the Ended modal nested inside the replay/history popup.
     */
    closeReplayEnded(state) {
      state.replayEndedActive = false;
    },

    /**
     * Reducer to close all popups.
     * Resets all popup states to false, ensuring no popups are open.
     * Preserves the intro popup state as it should only appear once at game start.
     * Preserves the connection popup state as it should not be closed by game events.
     * Useful for cleanup or when game state changes require all popups to be closed.
     */
    closeAllPopups(state) {
      return {
        ...initialState,
        introPopupActive: state.introPopupActive, // Preserve current intro popup state
        userInterfaceHidden: state.userInterfaceHidden,
        userInterfaceDisabled: state.userInterfaceDisabled,
        connectionPopupActive: state.connectionPopupActive, // Preserve connection popup state
        infoPopupActive: state.infoPopupActive, // Preserve info popup state (for Free Rounds messages during win animations)
        infoPopupKey: state.infoPopupKey, // Preserve info popup key
        infoPopupData: state.infoPopupData, // Preserve info popup data
        symbolPopupActive: null, // Close any open symbol popup
        gameRulesActive: false, // Close game rules popup
        paytableActive: false, // Close paytable popup
        // ── The replay surfaces SURVIVE a win ──
        //
        // This reducer rebuilds from initialState, so without naming them
        // here all three replay flags are wiped — and it is dispatched on
        // every ANIMATED win (useWinController, 5 sites). Replaying a round
        // that ends in a big win therefore closed the History board and the
        // summary modal the moment the celebration started, which is why the
        // table came back on a losing replay and never on a winning one.
        //
        // Nothing is lost for live play: the board blocks the play button via
        // selectAnyPopupActive, so it cannot be open when a spin — and
        // therefore a win animation — begins. Closing it here was only ever
        // reachable during a replay, where it is wrong.
        replayActive: state.replayActive,
        replayEnterActive: state.replayEnterActive,
        replayEndedActive: state.replayEndedActive,
        autoplayPopupActive: false, // Close autoplay popup
        bonusBuyActive: false, // Close bonus buy popup
      };
    },

    /**
     * Reducer to open the credits popup.
     * Sets creditsPopupActive to true.
     */
    openCreditsPopup(state) {
      state.symbolPopupActive = null;
      state.creditsPopupActive = true;
    },

    /**
     * Reducer to close the credits popup.
     * Sets creditsPopupActive to false.
     */
    closeCreditsPopup(state) {
      state.creditsPopupActive = false;
    },

    /**
     * Reducer to open the session expiration popup.
     * Closes all other popups and menus, then opens the expiration popup.
     * This ensures only the session expiration popup is visible when session expires.
     */
    openExpirationPopup(state) {
      // Close all popups and menus
      state.introPopupActive = false;
      state.betSelectPopupActive = false;
      state.autoplayPopupActive = false;
      state.leaveGamePopupActive = false;
      state.menuActive = false;
      state.gameRulesActive = false;
      state.paytableActive = false;
      state.settingsActive = false;
      state.replayActive = false;
      state.creditsPopupActive = false;
      state.symbolPopupActive = null;

      // Open only the expiration popup
      state.expirationPopupActive = true;
    },

    /**
     * Reducer to close the session expiration popup.
     * Sets expirationPopupActive to false.
     */
    closeExpirationPopup(state) {
      state.expirationPopupActive = false;
    },

    setUserInterfaceDisabled(state, action) {
      state.userInterfaceDisabled = action.payload;
    },

    setBuyFreeSpinsPopupActive(state, action) {
      state.buyFreeSpinsPopupActive = action.payload;
    },


    openErrorPopup(state, action) {
      state.symbolPopupActive = null;
      state.errorPopupActive = true;
      state.errorPopupCode = action.payload; // Store error code
    },

    closeErrorPopup(state) {
      state.errorPopupActive = false;
      state.errorPopupCode = null;
    },

    /**
     * Reducer to open the info popup with a message key and optional data.
     * @param {object} action.payload - Object with {key: string, data: object}
     */
    openInfoPopup(state, action) {
      state.symbolPopupActive = null;
      state.infoPopupActive = true;
      state.infoPopupKey = action.payload.key;
      state.infoPopupData = action.payload.data || {};
    },

    /**
     * Reducer to close the info popup.
     * Clears all info popup state.
     */
    closeInfoPopup(state) {
      state.infoPopupActive = false;
      state.infoPopupKey = null;
      state.infoPopupData = {};
    },

    openBuyFreeSpinsPopup(state) {
      state.symbolPopupActive = null;
      state.buyFreeSpinsPopupActive = true;
    },

    closeBuyFreeSpinsPopup(state) {
      state.buyFreeSpinsPopupActive = false;
    },

    openConnectionPopup(state) {
      state.symbolPopupActive = null;
      state.connectionPopupActive = true;
    },

    closeConnectionPopup(state) {
      state.connectionPopupActive = false;
    },

    /**
     * Reducer to open the sound adjustment popup.
     * Sets soundAdjustmentPopupActive to true.
     */
    openSoundAdjustmentPopup(state) {
      state.soundAdjustmentPopupActive = true;
    },

    /**
     * Reducer to close the sound adjustment popup.
     * Sets soundAdjustmentPopupActive to false.
     */
    closeSoundAdjustmentPopup(state) {
      state.soundAdjustmentPopupActive = false;
    },

    /**
     * Reducer to open a symbol popup.
     * Sets symbolPopupActive to the symbolId and closes any other open popup.
     */
    openSymbolPopup(state, action) {
      state.symbolPopupActive = action.payload;
      state.miniPaytablePopupActive = true; // Set MiniPaytable popup as open
    },

    /**
     * Reducer to close the currently open symbol popup.
     * Sets symbolPopupActive to null.
     */
    closeSymbolPopup(state) {
      state.symbolPopupActive = null;
      state.miniPaytablePopupActive = false; // Set MiniPaytable popup as closed
    },

    setSymbolPopupBlocked(state, action) {
      state.symbolPopupBlocked = action.payload;
      // Close any open symbol popup when blocking is activated
      if (action.payload) {
        state.symbolPopupActive = null;
      }
    },

    openTurboSuggestionPopup(state) {
      state.symbolPopupActive = null;
      state.turboSuggestionPopupActive = true;
    },

    closeTurboSuggestionPopup(state) {
      state.turboSuggestionPopupActive = false;
    },



    /**
     * Reducer to open the Hold and Win resume popup.
     * Sets holdAndWinResumePopupActive to true.
     */
    openHoldAndWinResumePopup(state) {
      state.symbolPopupActive = null;
      state.holdAndWinResumePopupActive = true;
    },

    /**
     * Reducer to close the Hold and Win resume popup.
     * Sets holdAndWinResumePopupActive to false.
     */
    closeHoldAndWinResumePopup(state) {
      state.holdAndWinResumePopupActive = false;
    },

    /**
     * Reducer to open the iOS fullscreen overlay.
     * Sets iosFullscreenOverlayActive to true.
     */
    openIosFullscreenOverlay(state) {
      state.iosFullscreenOverlayActive = true;
    },

    /**
     * Reducer to close the iOS fullscreen overlay.
     * Sets iosFullscreenOverlayActive to false.
     */
    closeIosFullscreenOverlay(state) {
      state.iosFullscreenOverlayActive = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // When any feature triggers (bonus, freeSpins, holdAndWin, royalCollect, etc.),
      // immediately close all popups to prevent them from staying open during bonus games
      .addCase(rgsPlay.fulfilled, (state, action) => {
        const { featureState } = action.payload;
        if (featureState?.phase === "intro") {
        // Close all user-openable popups — same logic as closeAllPopups reducer
          state.betSelectPopupActive = false;
          state.autoplayPopupActive = false;
          state.leaveGamePopupActive = false;
          state.menuActive = false;
          state.gameRulesActive = false;
          state.paytableActive = false;
          state.settingsActive = false;
          state.replayActive = false;
          state.creditsPopupActive = false;
          state.symbolPopupActive = null;
          state.miniPaytablePopupActive = false;
          state.turboSuggestionPopupActive = false;
          state.bonusBuyActive = false;
          state.soundAdjustmentPopupActive = false;
        }
      })
      // Listen for rgsPlay rejection to open error popup
      .addCase(rgsPlay.rejected, (state, action) => {
        const errorData = action.payload;

        // Parse error response: {"errors":[{"code":301,"desc":"not_enough_money"}]}
        if (
          errorData?.errors &&
          Array.isArray(errorData.errors) &&
          errorData.errors.length > 0
        ) {
          const firstError = errorData.errors[0];
        // Open error popup with the error code
          state.errorPopupActive = true;
          state.errorPopupCode = firstError.code;
        } else {
        // Generic network errors (timeouts, disconnects, non-structured failures)
        // MUST still show the error popup — otherwise the user sees stuck/frozen reels
        // with no feedback and no way to recover
          state.errorPopupActive = true;
          state.errorPopupCode = "connection";
        }
      })
      // Catch ANY rejected thunk carrying a structured errors array (e.g. initializeGame,
      // refreshGameData, heartbeatSync). Opens the error popup with the proper code.
      // This avoids circular imports from gameSettingsSlice.
      .addMatcher(
        (action) =>
          action.type.endsWith("/rejected") &&
          action.type !== rgsPlay.rejected.type && // already handled above
          action.payload?.errors &&
          Array.isArray(action.payload.errors) &&
          action.payload.errors.length > 0,
        (state, action) => {
          const firstError = action.payload.errors[0];
          state.errorPopupActive = true;
          state.errorPopupCode = firstError.code;
        }
      );
  },
});

export const selectMenuActive = (state) => state.userInterface.menuActive;

export const selectIntroPopupActive = (state) =>
  state.userInterface.introPopupActive;

export const selectUserInterfaceDisabled = (state) =>
  state.userInterface.userInterfaceDisabled;

export const selectPaytableActive = (state) =>
  state.userInterface.paytableActive;

export const selectGameRulesActive = (state) =>
  state.userInterface.gameRulesActive;

export const selectSettingsActive = (state) =>
  state.userInterface.settingsActive;

export const selectReplayActive = (state) =>
  state.userInterface.replayActive;

export const selectReplayEnterActive = (state) =>
  state.userInterface.replayEnterActive;

export const selectReplayEndedActive = (state) =>
  state.userInterface.replayEndedActive;

export const selectCreditsPopupActive = (state) =>
  state.userInterface.creditsPopupActive;


export const selectExpirationPopupActive = (state) =>
  state.userInterface.expirationPopupActive;

export const selectBuyFreeSpinsPopupActive = (state) =>
  state.userInterface.buyFreeSpinsPopupActive;

export const selectConnectionPopupActive = (state) =>
  state.userInterface.connectionPopupActive;

export const selectSoundAdjustmentPopupActive = (state) =>
  state.userInterface.soundAdjustmentPopupActive;

export const selectSymbolPopupActive = (state) =>
  state.userInterface.symbolPopupActive;

export const selectSymbolPopupBlocked = (state) =>
  state.userInterface.symbolPopupBlocked;

export const selectMiniPaytablePopupActive = (state) =>
  state.userInterface.miniPaytablePopupActive;

export const selectTurboSuggestionPopupActive = (state) =>
  state.userInterface.turboSuggestionPopupActive;

export const selectAutoplayPopupActive = (state) =>
  state.userInterface.autoplayPopupActive;

export const selectBetSelectPopupActive = (state) =>
  state.userInterface.betSelectPopupActive;

export const selectBonusBuyActive = (state) =>
  state.userInterface.bonusBuyActive;



export const selectHoldAndWinResumePopupActive = (state) =>
  state.userInterface.holdAndWinResumePopupActive;

export const selectIosFullscreenOverlayActive = (state) =>
  state.userInterface.iosFullscreenOverlayActive;

export const selectLeaveGamePopupActive = (state) =>
  state.userInterface.leaveGamePopupActive;

// Export the actions generated by `createSlice` for use in components or middleware
// These actions can be dispatched to update the state
export const {
  openBetSelectPopup,
  closeBetSelectPopup,
  closeIntroPopup,
  openAutoplayPopup,
  closeAutoplayPopup,
  openLeaveGamePopup,
  closeLeaveGamePopup,
  openMenu,
  closeMenu,
  openGameRules,
  closeGameRules,
  openPaytable,
  closePaytable,
  openSettings,
  closeSettings,
  openReplay,
  closeReplay,
  openReplayEnter,
  closeReplayEnter,
  openReplayEnded,
  closeReplayEnded,
  closeAllPopups,
  openCreditsPopup,
  closeCreditsPopup,
  openErrorPopup,
  closeErrorPopup,
  openBuyFreeSpinsPopup,
  closeBuyFreeSpinsPopup,
  openExpirationPopup,
  closeExpirationPopup,
  setUserInterfaceDisabled,
  setBuyFreeSpinsPopupActive,
  openConnectionPopup,
  closeConnectionPopup,
  openSoundAdjustmentPopup,
  closeSoundAdjustmentPopup,
  openSymbolPopup,
  closeSymbolPopup,
  setSymbolPopupBlocked,
  openTurboSuggestionPopup,
  closeTurboSuggestionPopup,
  openBonusBuy,
  closeBonusBuy,

  openHoldAndWinResumePopup,
  closeHoldAndWinResumePopup,
  openInfoPopup,
  closeInfoPopup,
  openIosFullscreenOverlay,
  closeIosFullscreenOverlay,
} = userInterfaceSlice.actions;

// Base selector to get the userInterface slice
const selectUserInterface = (state) => state.userInterface;

// Memoized selector to check if any popup is currently active
// Only recalculates when userInterface slice changes, not on every state update
export const selectAnyPopupActive = createSelector(
  [selectUserInterface],
  (ui) => {
    return (
      ui.introPopupActive ||
      ui.betSelectPopupActive ||
      ui.autoplayPopupActive ||
      ui.leaveGamePopupActive ||
      // ui.menuActive removed - menu should not block play button
      ui.gameRulesActive ||
      ui.paytableActive ||
      ui.settingsActive ||
      ui.replayActive ||
      // The replay Enter/Ended modals block play like every other popup.
      // In-game they were covered incidentally by replayActive (the History
      // board behind them), but the standalone replay link never opens that
      // board — leaving spacebar able to start the round behind the Enter
      // modal, and the Ended modal to stack on top of a still-open Enter.
      ui.replayEnterActive ||
      ui.replayEndedActive ||
      ui.creditsPopupActive ||
      ui.expirationPopupActive ||
      ui.buyFreeSpinsPopupActive ||
      ui.connectionPopupActive ||
      ui.bonusBuyActive ||
      ui.errorPopupActive ||
      ui.infoPopupActive ||
      (FEATURES_CONFIG.TURBO_SUGGESTION.ENABLED &&
        ui.turboSuggestionPopupActive)
    );
  },
);

export const selectErrorPopupActive = (state) =>
  state.userInterface.errorPopupActive;
export const selectErrorPopupCode = (state) =>
  state.userInterface.errorPopupCode;
export const selectInfoPopupActive = (state) =>
  state.userInterface.infoPopupActive;
export const selectInfoPopupKey = (state) => state.userInterface.infoPopupKey;
export const selectInfoPopupData = (state) => state.userInterface.infoPopupData;

// Export the reducer function to be included in the Redux store
// The store will use this reducer to handle actions related to user interface
export default userInterfaceSlice.reducer;
